import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import moment from 'moment'
import React, { useContext, useEffect, useState } from 'react'
import { displayEventLocation } from '../../../../helpers/eventAddress'
import Avatar from '@mui/material/Avatar'
import { useParams } from 'react-router-dom'
import Loading, { LoadingSmall } from '../../../loading/loadingPage'
import API from '../../../../services/API'
import DetailsBox, { DetailRow } from '../../../../components/Cards/DetailsBox'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import DangerousIcon from '@mui/icons-material/Dangerous'
import CustomDataGrid from '../../../../components/DataGrid/CustomDataGrid'
import SmallModal from '../../../../components/Modals/SmallModal'
import EMP from '../DocumentGenerator/EMP'
import { TenantSettingsContext, UserContext } from '../../../../App'
import MediumModal from '../../../../components/Modals/MediumModal'
import S3DocumentUploadDisplay from '../../../../components/images/S3DocumentUploadDisplay'
import DownloadIcon from '@mui/icons-material/Download'
import PDFViewer from '../../../../components/Modals/PDFViewer'
import { useDocumentTitle } from '../../../../helpers/DocumentTitleManager'

const shiftIsCovered = (shift) =>
  !!shift?.assignedTo ||
  !!shift?.assignedSubcontractor ||
  !!shift?.assignedToSubcontractor

const getAssignedShiftLabel = (shift) => {
  if (shift?.assignedTo) {
    return `${shift.assignedTo.userAccount?.firstName ?? ''} ${
      shift.assignedTo.userAccount?.lastName ?? ''
    }`.trim()
  }

  if (shift?.assignedSubcontractor) {
    return `${shift.assignedSubcontractor.userAccount?.firstName ?? ''} ${
      shift.assignedSubcontractor.userAccount?.lastName ?? ''
    }`.trim()
  }

  if (shift?.assignedToSubcontractor) return 'Subcontractor'

  return 'Unassigned'
}

const getAssignedShiftPhotograph = (shift) =>
  shift?.assignedTo?.userAccount?.photograph ||
  shift?.assignedSubcontractor?.userAccount?.photograph

const getAssignedShiftInitials = (shift) => {
  const label = getAssignedShiftLabel(shift)
  return label
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
}

const ViewEmployee = ({ employee, onClose }) => {
  return (
    <MediumModal open={!!employee} onClose={onClose}>
      <Stack gap={1}>
        <Typography variant="title2" textAlign="center">
          {employee?.userAccount?.firstName} {employee?.userAccount?.lastName}
        </Typography>
        <DetailRow label="Clinical Role" value={employee?.clinicalRole} />
        <DetailRow label="Staff ID" value={employee?.pinNumber} />
        <DetailRow
          label="Phone"
          value={employee?.personnelInfo?.phoneNumber1 || ''}
        />
        <DetailRow
          label="Radio Callsign"
          value={employee?.personnelInfo?.radioCallSign}
        />
        <Button color="error" variant="outlined" onClick={() => onClose()}>
          Close
        </Button>
      </Stack>
    </MediumModal>
  )
}

const BasicView = () => {
  const { id } = useParams()
  const [loading, setLoading] = useState(true)
  const [loadingVehicles, setLoadingVehicles] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [event, setEvent] = useState(null)
  const [viewEmployee, setViewEmployee] = useState(null)
  const { user } = useContext(UserContext)
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const { setPageTitle } = useDocumentTitle()
  const [selecteds3Doc, setSelectedS3Doc] = useState(null)
  const [deployedVehicles, setDeployedVehicles] = useState(null)

  useEffect(() => {
    API.get('/event-basic-info/' + id)
      .then((res) => {
        if (res.status === 200) {
          setEvent(res.data)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [])

  const daysAr = event
    ? event.shifts.reduce((accumulator, shift) => {
        const dayStr = moment(shift.start).startOf('day').toISOString()
        if (!accumulator[dayStr]) accumulator[dayStr] = []
        accumulator[dayStr].push(shift)
        return accumulator
      }, {})
    : {}

  useEffect(() => {
    API.post('/fleet/getvehiclesdeployed', {
      linkedEvent: id,
    })
      .then((res) => {
        if (res.status === 200) {
          setDeployedVehicles(res.data)
        }
      })
      .finally(() => setLoadingVehicles(false))
  }, [id])

  useEffect(() => {
    if (event?.title) {
      setPageTitle(event.title)
    }
  }, [event?.title, setPageTitle])

  return (
    <Stack gap={1}>
      {fetchError && (
        <Alert severity="error">
          Unable to load event. Please try again later.
        </Alert>
      )}
      {loading || loadingVehicles ? (
        <LinearProgress />
      ) : (
        <Stack gap={1}>
          <Typography variant="title2">{event.title}</Typography>
          <DetailsBox title="Event Information" component={Paper} padding={1}>
            <DetailRow
              label="Start"
              value={moment(event.startDate).format('Do MMM YYYY - HH:mm')}
            />
            <DetailRow
              label="End"
              value={moment(event.endDate).format('Do MMM YYYY - HH:mm')}
            />
            <DetailRow label="Organiser" value={event.organisation?.orgName} />
            <DetailRow label="Event Type" value={event.eventType} />
            <DetailRow
              label="Event Location"
              value={displayEventLocation(event)}
            />
            <DetailRow label="What3Words" value={event?.opsPlan?.what3Words} />
          </DetailsBox>
          <DetailsBox
            title="Medical Plan & Documents"
            component={Paper}
            padding={1}
          >
            {!event.EMPApproved ? (
              <Alert severity="info">
                The Event Medical Plan has not yet been Approved.
              </Alert>
            ) : (
              <EMP
                user={{ firstName: user.firstName, lastName: user.lastName }}
                status={event.EMPApproved ? 'Approved' : 'Draft'}
                eventID={event._id}
                orgSettings={orgSettings}
              />
            )}
            <CustomDataGrid
              rows={event.documents ?? []}
              columns={[
                {
                  field: 'author',
                  headerName: 'Uploaded By',
                  valueGetter: (value) => value._id,
                  renderCell: ({ row }) =>
                    `${row.author.firstName} ${row.author.lastName}`,
                  minWidth: 200,
                  flex: 1,
                },
                {
                  field: 'documentName',
                  headerName: 'Title',
                  minWidth: 150,
                },
                {
                  field: 's3key',
                  headerName: 'Download',
                  renderCell: () => (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      width="100%"
                      height="100%"
                    >
                      <DownloadIcon />
                    </Box>
                  ),
                },
              ]}
              onRowClick={({ row }) => setSelectedS3Doc(row.s3key)}
              getRowId={(row) => row._id}
              slots={{
                footer: () => null,
                toolbar: () => null,
              }}
            />
            <PDFViewer
              open={!!selecteds3Doc}
              onClose={() => setSelectedS3Doc(null)}
              s3key={selecteds3Doc}
            />
          </DetailsBox>
          <DetailsBox title="Shifts" component={Paper} padding={1}>
            {Object.keys(daysAr)
              .sort((a, b) => (moment(a) < moment(b) ? -1 : 1))
              .map((day) => {
                const totalShifts = daysAr[day].length
                const filledShifts = daysAr[day].filter(shiftIsCovered).length
                const isGood = totalShifts === filledShifts
                return (
                  <Accordion
                    key={day}
                    sx={{
                      border: 'solid 2px',
                      borderColor: isGood ? '#095256' : '#C3423F',
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: '#fff' }} />}
                      sx={{
                        backgroundColor: isGood ? '#095256' : '#C3423F',
                        color: 'white',
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-around"
                        flexDirection="column"
                      >
                        <Typography
                          variant="title2"
                          fontSize="1rem"
                          sx={{ color: '#fff' }}
                        >
                          {moment(day).format('ddd Do MMM YYYY')}
                        </Typography>
                        <Typography variant="caption" color="#fff">
                          {filledShifts} / {totalShifts} shifts filled
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{ padding: 0, overFlowX: 'scroll', borderRadius: 0 }}
                    >
                      <CustomDataGrid
                        slots={{ footer: () => null, toolbar: () => null }}
                        rows={daysAr[day]}
                        disableRowSelectionOnClick
                        onRowClick={({ row }) =>
                          row.assignedTo && setViewEmployee(row.assignedTo)
                        }
                        columns={[
                          {
                            field: 'start',
                            headerName: 'Start',
                            renderCell: ({ row }) =>
                              moment(row.start).format('HH:mm'),
                            minWidth: 100,
                          },
                          {
                            field: 'end',
                            headerName: 'End',
                            renderCell: ({ row }) =>
                              moment(row.end).format('HH:mm'),
                            minWidth: 100,
                          },
                          {
                            field: 'clinicalRole',
                            headerName: 'Role',
                            minWidth: 200,
                            flex: 1,
                          },
                          {
                            field: 'eventAssignment',
                            headerName: 'Event Role',
                            minWidth: 200,
                            flex: 1,
                          },
                          {
                            field: 'assignedTo',
                            headerName: 'Staff',
                            align: 'center',
                            headerAlign: 'center',
                            valueGetter: (value) => value?._id,
                            renderCell: ({ row }) => (
                              <Box
                                height="100%"
                                width="100%"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                              >
                                {shiftIsCovered(row) ? (
                                  <Avatar
                                    src={getAssignedShiftPhotograph(row)}
                                    sx={{ borderRadius: 1 }}
                                  >
                                    {getAssignedShiftInitials(row)}
                                  </Avatar>
                                ) : (
                                  <DangerousIcon
                                    color="error"
                                    fontSize="small"
                                  />
                                )}
                              </Box>
                            ),
                          },
                        ]}
                      />
                    </AccordionDetails>
                    <ViewEmployee
                      employee={viewEmployee}
                      onClose={() => setViewEmployee(null)}
                    />
                  </Accordion>
                )
              })}
          </DetailsBox>
          <DetailsBox title="Vehicles" component={Paper} padding={1}>
            <CustomDataGrid
              maxHeight={300}
              rows={deployedVehicles}
              slots={{
                toolbar: () => null,
                footer: () => null,
              }}
              hideToolbar
              disableRowSelectionOnClick
              getRowId={(params) => params._id}
              columns={[
                {
                  field: 'callSign',
                  headerName: 'Call Sign',
                  minWidth: 200,
                  valueGetter: (value, row) => row.vehicle.callSign,
                },
                {
                  field: 'driverTo',
                  headerName: 'Driver To',
                  minWidth: 150,
                  valueGetter: (value, row) =>
                    row?.driverTo &&
                    row?.driverTo?.firstName + ' ' + row?.driverTo?.lastName,
                  flex: 1,
                },
                {
                  field: 'driverFrom',
                  headerName: 'Driver From',
                  minWidth: 150,
                  valueGetter: (value, row) =>
                    row.driverFrom &&
                    row?.driverFrom?.firstName +
                      ' ' +
                      row?.driverFrom?.lastName,
                  flex: 1,
                },
                {
                  field: 'start',
                  headerName: 'Booked From',
                  renderCell: ({ value }) =>
                    moment(value).format('Do MMM HH:mm'),
                  minWidth: 150,
                  headerAlign: 'center',
                  align: 'center',
                },
                {
                  field: 'end',
                  headerName: 'Booked Until',
                  renderCell: ({ value }) =>
                    moment(value).format('Do MMM HH:mm'),
                  minWidth: 150,
                  headerAlign: 'center',
                  align: 'center',
                },
              ]}
            />
          </DetailsBox>
        </Stack>
      )}
    </Stack>
  )
}

export default BasicView
